import type { StrictTupleKey } from 'swr/_internal';
import useSWRImmutable from 'swr/immutable';
import { ComparableSubscription } from '~/models/subscription-comparison/ComparableSubscriptions';
import { useFeatureFLags, useViaplayBruddFeatures } from '../../../hooks/useFeatureFlags';
import { getSubscriptionsForPartner } from './api';
import { comparableSubscriptionsStaticData } from './placeholderSubscriptionComparisonData';

const SUB_SORT_ORDER_MAP = {
  rikspakken: 1,
  strømmepakken: 2,
  sportspakken: 3,
  fleksipakken: 4
} as const;

export const useComparableSubscriptions = (partnerAlias: string, dwellingUnit: string) => {
  
  const flags = useFeatureFLags();
  const isViaplayBreachActive = useViaplayBruddFeatures(flags);
  const cacheKey: StrictTupleKey = ['get-subscription', partnerAlias, dwellingUnit, isViaplayBreachActive];

  return useSWRImmutable(cacheKey, async () => {
    const res = await getSubscriptionsForPartner(partnerAlias, dwellingUnit);
    return res.map((sub) => {
      const staticData = comparableSubscriptionsStaticData(isViaplayBreachActive).filter((s) => s.subscriptionIccProductId === sub.subscriptionIccProductId)[0];
      const compSub: ComparableSubscription = {
        ...staticData,
        ...sub,
        discountPercentage: getDiscountPercentage(sub.originalPrice, sub.currentPrice),
        readMoreUrl: getReadMoreUrl(partnerAlias, sub.campaignUrl),
        sortOrder: SUB_SORT_ORDER_MAP[sub.name.toLowerCase() as keyof typeof SUB_SORT_ORDER_MAP] || 5, // Default to 5 if not found
      };
      return compSub;
    }).sort((a, b) => a.sortOrder - b.sortOrder);
  });
};

const getDiscountPercentage = (originalPrice: number, currentPrice: number) => {
  return Math.floor((1 - currentPrice / originalPrice) * 100);
};

const getReadMoreUrl = (partnerAlias: string, campaignUrl: string) => {
  // TODO: Should check if it's fiber in a better way
  // Best way would be by businessUnitId, but then we need to fetch partner data
  // in the API using SalesPartnerRegistry service (to get the bUnitId from Sanity)
  if (partnerAlias === 'kampanje') {
    return `https://www.rikstv.no/tv-pakker/${campaignUrl}`;
  }
  return `https://www.rikstv.no/rikstv-via-fiber/partner/${partnerAlias}/${campaignUrl}/`;
};
